import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { pathFor } from '../routes/paths';
import SEO from '../seo/SEO';
import { useNavigate } from 'react-router-dom';
import ContactSection from '../components/ContactSection';

const Page = styled.section`
  padding: 0; /* remove bottom white band before footer */
  background: #ffffff;
`;

// Accent color reused across sections (buttons, highlights)
const ACCENT = '#67313a';

/* HERO full-screen section (uses an image placeholder for the future video) */
const Hero = styled.section`
  position: relative;
  padding-top: 100px; /* account for fixed header so content isn't hidden */
  min-height: calc(100vh - 1px); /* height of visible area below the header */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  overflow: hidden;
`;

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.55);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4); /* 40% black overlay for readability */
  z-index: 2; /* above video, below content */
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  padding: 0 24px;
  z-index: 3; /* above overlay */
`;

const HeroTitle = styled(motion.h1)`
 font-family: 'Tilt Warp', cursive;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-size: clamp(24px, 4.2vw, 46px);
  margin: 0 0 14px;
`;

const HeroSub = styled(motion.p)`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(28px, 1.4vw, 16px);
  line-height: 1.8;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
  color: rgba(255,255,255,0.9);
`;

/* Cover-fit iframe for hero background video */
const HeroVideo = styled.iframe`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Scale logic to achieve CSS 'cover' for a 16:9 player */
  width: 177.78vh;  /* 100 * (16/9) -> fills by height on tall screens */
  height: 100vh;
  @media (min-aspect-ratio: 16/9) {
    width: 100vw;    /* fills by width on wide screens */
    height: 56.25vw; /* 100 * (9/16) */
  }
  border: 0;
  background: #000;
  z-index: 1;
  pointer-events: none; /* keep interactions on our overlay/content */
`;

/* UnmuteButton removed per design request */

/* Marble band with monogram and title */
const Marble = styled.section`
  background: url('/images/MArbre Blanc - 1500 px.webp') center/cover no-repeat;
  padding: 68px 20px 84px;
`;

const MarbleInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
`;

const MonoCircle = styled.div`
  width: 78px;
  height: 78px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;

  img { width: 50px; height: auto; display: block; }
`;

const MarbleTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(18px, 3.4vw, 28px);
  letter-spacing: 1.6px;
  color: #2b2b2b;
  text-transform: uppercase;
  margin: 0;
`;

/* Values grid styles (match mock colors) */
const ValuesSection = styled.section`
  padding: 70px 0 20px;
  background: #fff;
  @media (max-width: 600px) {
    padding: 50px 0 20px; /* remove 20px after the marble banner on mobile */
  }
`;

const ValuesGrid = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 60px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px 80px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const ValueCard = styled.div``;

const ValueNum = styled.div`
  font-family: 'Tiro Bangla', serif;
  font-style: italic;
  color: #8d1e26; /* deep red */
  font-size: 30px;
  margin-bottom: 6px;
`;

const ValueTitle = styled.h4`
  font-family: 'Tiro Bangla', serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #6c6764; /* grey title */
  margin: 0 0 12px;
  font-size: 18px;
`;

const Accent = styled.div`
  width: 64px;
  height: 3px;
  background: #8d1e26; /* deep red */
  margin: 10px 0 16px;
`;

const ValueText = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  color: #6f6b68;
  line-height: 1.9;
  font-size: 15px;
  margin: 0;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 60px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Title = styled.h2`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(28px, 5vw, 42px);
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #67313a;
  margin: 0 0 12px;
`;

const Lead = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #a89b97;
  margin: 0 0 18px;
`;

const Copy = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 16px;
  line-height: 1.9;
  color: #5f5a57;
  margin-bottom: 14px;
`;

/* Video section (single column, centered, 16:9) */
const VideoSection = styled.section`
  padding: 20px 0 70px;
  background: #fff;
`;

const VideoInner = styled.div`
  max-width: 1100px;
  margin: 1px auto 50px;
  padding: 0 60px;
`;

const VideoRatio = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 */
  box-shadow: 0 12px 28px rgba(0,0,0,0.18);
`;

const VideoIframe = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #000;
`;

/* ===== Distinctions Row (logos) ===== */
const Distinctions = styled.section`
  background: #f6efe8;
  padding: 30px 0 60px;
`;

const DistBar = styled.div`
  width: 31%;
  height: 2px;
  background: #e0d8d2;
  margin: 25px auto 0;
  opacity: 0.9;

  @media (max-width: 900px) {
    width: 82%;
  }
`;

const DistRow = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
  align-items: center;
  padding-top: 22px;
  flex-wrap: wrap;
`;

const DistBadge = styled.img`
  height: 30px;
  opacity: 0.9;
  filter: grayscale(30%);
`;

/* ===== Process & Investment ===== */
const ProcessWrap = styled.section`
  padding: 80px 0;
  background: #ffffff;
`;

const ProcessHeader = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  color: #67313a;
  text-align: left;
  max-width: 1200px;
  margin: 0 auto 24px;
  padding: 0 50px;
  @media (max-width: 968px) {
    padding: 0 60px; /* match ProcessGrid padding on mobile */
  }
`;

const ProcessGrid = styled.div`
  max-width: 1200px;
  margin: 10px auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 50px;
  padding: 20px 50px 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 20px 60px 20px; /* unify L/R to 60px like ValuesGrid; maintain reduced bottom */

  }
  @media (max-width: 600px) {
    padding: 20px 60px 20px; /* unify L/R to 60px like ValuesGrid; maintain reduced bottom */
  }
`;

const Hint = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  color: #9a9a9a;
  margin-bottom: 12px;
`;

const Accordion = styled.div``;

const Item = styled.div`
  border: 1px solid #e6e0db;
  margin-bottom: 10px;
`;

const ItemHeader = styled.button`
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: none;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-family: 'Tiro Bangla', serif;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #6b6b6b;
`;

const Icon = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: #b8a398;
`;

const ItemBody = styled.div`
  padding: 0 16px 14px;
  font-family: 'Barlow Semi Condensed', sans-serif;
  color: #6a6a6a;
  font-size: 15px;
  line-height: 1.75;
`;

const ProcessImage = styled.img`
  width: 100%;
  height: 78%;
  object-fit: cover;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08);
`;

const Investment = styled.section`
  background: url('/images/MArbre Blanc - 1500 px.webp') center/cover no-repeat;
  padding: 130px 0;
  @media (max-width: 600px) {
    padding: 130px 0 110px; /* remove 20px below the Inquire button on mobile */
  }
`;

const InvestGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 430px 1fr;
  gap: 90px;
  padding: 0 60px;
  align-items: start;

  @media (max-width: 1060px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 968px) {
    padding: 0 40px;
  }
  @media (max-width: 600px) {
    padding: 0 60px; /* unify L/R to 60px like ValuesGrid on mobile */
  }
`;

const InvestImage = styled.img`
  width: 85%;
  margin-left: 20%;
  height: 530px;
  object-fit: cover;
  box-shadow: 0 15px 35px rgba(0,0,0,0.12);
  display: block;

  @media (max-width: 1060px) {
    width: 92%;
    margin: 0 auto 20px;
    height: auto;
  }
`;

const InvestText = styled.div`
  align-self: start;
  display: grid;
  justify-items: center;
  text-align: center;
  margin-top: 50px;

  @media (max-width: 1060px) {
    margin-top: 18px;
  }
`;

const InvestTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  color: #6b6b6b;
  font-size: clamp(32px, 5.2vw, 50px);
  letter-spacing: 0.5px;
  text-align: center;
  margin-bottom: 18px;
`;

const InvestP = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 15px;
  line-height: 1.85;
  color: #6f6a68;
  max-width: 520px;
  text-align: center;
  margin: 10px auto 20px;
`;

const InvestButton = styled(motion.button)`
  margin-top: 10px;
  background: ${ACCENT};
  border: none;
  padding: 10px 22px;
  color: #ffffff;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 2px;
`;

/* Testimonial full-bleed section */
const TestimonialSection = styled.section`
  position: relative;
  background: url('/images/Wedding videographer - Lino Ludovic (3).webp') center/cover no-repeat;
  min-height: 520px;
  padding: 120px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const TestimonialOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.30); /* 30% black overlay */
`;

const TestimonialInner = styled.div`
  position: relative;
  max-width: 980px;
  text-align: center;
  padding: 0 24px;
`;

const TestimonialText = styled.blockquote`
  font-family: 'Tiro Bangla', serif;
  text-transform: uppercase;
  font-size: clamp(14px, 2.2vw, 12px);
  line-height: 1.7;
  letter-spacing: 1px;
  margin: 0;
  color: #ffffff;
`;

const TestimonialAuthor = styled.div`
  margin-top: 22px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  letter-spacing: 4px;
  font-size: clamp(12px, 1.1vw, 16px);
  color: #ffffff;
`;

const VideographerPage = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(-1);
  const heroVideoRef = useRef(null);
  const review = lang === 'fr'
    ? {
        text:
          "Au‑delà des photos, Ludo nous a accompagnés du début à la fin dans la préparation du mariage. Son dévouement n’a d’égal que sa gentillesse envers nous et nos invités tout au long de la journée. Par sa personnalité, il nous a mis en confiance (et ce n’était pas gagné !) et a fait ressortir l’atmosphère du grand jour dans chaque image.",
        author: 'Jade',
      }
    : {
        text:
          "Beyond the photos, Ludo supported us from start to finish throughout our wedding planning. His dedication is matched only by his kindness toward us and our guests all day long. Through his personality he made us feel at ease (and that wasn’t easy!) and brought out the atmosphere of the big day in every image.",
        author: 'Jade',
      };
  const values = lang === 'fr'
    ? [
        {
          title: 'HÉRITAGE',
          text:
            "La photographie préserve ce qui se voit, le film préserve ce qui se ressent. Ensemble, ils composent une narration continue où images et voix, gestes et atmosphères forgent l’héritage durable de votre célébration. En unissant ma vision à celle d’un vidéaste de confiance, nous vous offrons le privilège de revivre votre histoire avec profondeur, émotion et élégance intemporelle.",
        },
        {
          title: 'NARRATION',
          text:
            "Plus qu’une vidéo de mariage, c’est un film cinématographique réalisé avec la même sensibilité éditoriale que vos photographies. Au‑delà de la chronologie du jour, il révèle l’essence de qui vous êtes. Des entretiens enregistrés avant le mariage, des moments intimes d’une célébration sur plusieurs jours et la texture de vos voix nourrissent une narration profondément personnelle et résonante.",
        },
        {
          title: 'HARMONIE',
          text:
            "En parfaite synergie, photographie et film s’enrichissent sans jamais s’empiéter. Aucun chevauchement, aucune rupture : une vision artistique commune au service de votre histoire. Notre duo garantit discrétion, fluidité et une expérience sans couture, pour que vous viviez pleinement chaque instant tandis que nous composons une narration vivante.",
        },
        {
          title: 'EXCLUSIVITÉ',
          text:
            "Les photographies deviennent des héritages, les films des souvenirs vivants. Les rires dans vos voix, les mots de vos proches, l’atmosphère de votre célébration sont préservés avec une profondeur que seul le film peut capter. Cette rare alliance de photographie et de cinéma crée un patrimoine complet, façonné avec art, à chérir pour les générations.",
        },
      ]
    : [
        {
          title: 'HERITAGE',
          text:
            'Photography preserves what is seen, film preserves what is felt. Together, they create a seamless narrative where images and voices, gestures and atmospheres compose a lasting legacy of your celebration. By uniting my vision with a trusted videographer, we offer you the privilege of reliving your story with depth, emotion, and timeless elegance.',
        },
        {
          title: 'NARRATIVE',
          text:
            'More than a wedding video, this is a cinematic film crafted with the same editorial sensibility as your photographs. Beyond the day’s chronology, it tells the essence of who you are. Interviews recorded before the wedding, intimate moments from multi‑day celebrations, and the sound of your voices all contribute to a narrative that is profoundly personal and deeply resonant.',
        },
        {
          title: 'HARMONY',
          text:
            'Working in complete synergy, photography and film enrich one another without intrusion. There is no overlap, no disruption, only a shared artistic vision that enriches your story. Our duo guarantees discretion, fluidity, and a seamless experience, so you can live each moment fully while we compose a living, breathing narrative of it.',
        },
        {
          title: 'EXCLUSIVITY',
          text:
            'Photographs become heirlooms, films become living memories. The laughter in your voices, the words of your loved ones, the atmosphere of your celebration are preserved with a depth only film can capture. This rare alliance of photography and cinema creates a complete heritage, crafted with artistry to be cherished for generations.',
        },
      ];
  // Accordion steps (modeled after ExperiencePage)
  const steps = lang === 'fr'
    ? [
        {
          title: 'Vous déplacez‑vous à l’étranger ?',
          body:
            'Oui, je vous accompagne partout dans le monde. Qu’il s’agisse d’une journée ou d’une célébration sur plusieurs jours, chaque moment s’inscrit dans une narration fluide et élégante.',
        },
        {
          title: 'Comment fonctionne le duo photo & film ?',
          body:
            'Travaillant en parfaite synergie, la photographie et le film s’enrichissent mutuellement sans jamais s’empiéter. Aucun chevauchement, aucune rupture : une vision artistique commune au service de votre histoire Notre duo vous garantit discrétion, fluidité et harmonie, pour que vous viviez chaque instant pleinement tandis que nous composons une narration vivante et vibrante.',
        },
        {
          title: 'Le film peut‑il inclure nos voix ?',
          body:
            'Absolument. Des entretiens, vœux ou discours peuvent être enregistrés pour enrichir la narration. Ces voix, mêlées aux images en mouvement, donnent vie à un film intime et profondément personnel.',
        },
        {
          title: 'Est‑ce uniquement pour le jour du mariage ?',
          body:
            'Pas du tout. Dîners de bienvenue, brunchs et moments intimistes peuvent être intégrés pour créer un récit continu et immersif, au‑delà du seul jour J.',
        },
        {
          title: 'comment recevrons-nous ces souvenirs ?',
          body:
            'Vos photographies sont livrées dans une galerie privée élégante et sécurisée, facile à partager avec vos invités. Votre film est présenté dans un format cinématographique, intemporel et conçu pour durer.',
        },
      ]
    : [
        {
          title: 'Do you travel abroad?',
          body:
            'Yes, I accompany you anywhere in the world. Whether it is a single day or a multi-day celebration, every moment is woven into a seamless, elegant narrative.',
        },
        {
          title: 'How does the photo and film duo work?',
          body:
            'We work in complete harmony, each with a distinct role, yet guided by a shared vision. This fluid collaboration ensures your story is told with depth, coherence, and elegance.',
        },
        {
          title: 'Can the film include our voices?',
          body:
            ' Absolutely. Interviews, vows, or toasts can be recorded to enrich the narrative. These voices, combined with moving images, create a film that feels alive and profoundly personal.',
        },
        {
          title: 'Is this only for the wedding day?',
          body:
            'Not at all. Welcome dinners, brunches, and intimate gatherings are often included, creating a continuous and immersive story that goes beyond the wedding itself.',
        },
        {
          title: 'How will we receive everything?',
          body:
            'Your photographs are delivered in an elegant, secure online gallery, easy to share with your guests. Your film is presented in a cinematic format, timeless and crafted to last for generations.',
        },
      ];

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const heroVideoSrc = `https://www.youtube.com/embed/meULynhjMVE?si=n9fO67vlZFhXAqL2&autoplay=1&mute=1&controls=0&loop=1&playsinline=1&rel=0&modestbranding=1&enablejsapi=1&playlist=meULynhjMVE&origin=${encodeURIComponent(origin)}`;

  // Unmute logic removed

  return (
    <Page>
      <SEO
        title={lang === 'fr' ? 'Vidéaste de mariage — Lino Ludovic' : 'Wedding Videographer — Lino Ludovic'}
        description={
          lang === 'fr'
            ? 'Photographie et film entremêlés en une narration intemporelle de votre amour.'
            : 'Photography and film woven together into a timeless narrative of your love.'
        }
        image="/images/Wedding Annecy Savoie.jpg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: lang === 'fr' ? 'Vidéaste de mariage' : 'Wedding Videographer'
        }}
      />
      {/* HERO with autoplay YouTube background */}
      <Hero>
        <HeroVideo
          ref={heroVideoRef}
          src={heroVideoSrc}
          title={lang === 'fr' ? 'Film de mariage — Hero' : 'Wedding film — Hero'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        <HeroOverlay />
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {lang === 'fr' ? 'VIVRE UNE HISTOIRE EN MOUVEMENT' : 'EXPERIENCE A LIVING STORY'}
          </HeroTitle>
          <HeroSub
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
          >
            {lang === 'fr'
              ? <>PHOTOGRAPHIE ET FILM ENTRELACÉS EN UNE <br/> NARRATION INTEMPORELLE DE VOTRE AMOUR.</>
              : <>PHOTOGRAPHY AND FILM WOVEN TOGETHER INTO A <br/> TIMELESS NARRATIVE OF YOUR LOVE.</>}
          </HeroSub>
        </HeroContent>
        {/* Unmute button removed */}
      </Hero>

      {/* Marble band */}
      <Marble>
        <MarbleInner>
          <MonoCircle>
            <img src="/images/BADGE Grand Bordeaux.webp" alt="LL monogram" />
          </MonoCircle>
          <MarbleTitle>
            {lang === 'fr' ? 'Créer une expérience singulière' : 'Crafting a Singular Experience'}
          </MarbleTitle>
        </MarbleInner>
      </Marble>

      {/* Four-value grid section */}
      <ValuesSection>
        <ValuesGrid>
          {values.map((v, i) => (
            <ValueCard key={i}>
              <ValueNum>{String(i + 1).padStart(2, '0')}</ValueNum>
              <ValueTitle>{v.title}</ValueTitle>
              <Accent />
              <ValueText>{v.text}</ValueText>
            </ValueCard>
          ))}
        </ValuesGrid>
      </ValuesSection>

      {/* Youtube video (single column) */}
      <VideoSection>
        <VideoInner>
          <VideoRatio>
            <VideoIframe
              src="https://www.youtube.com/embed/JEqWKkLcjQA?si=EPV_kvGs194OMJbu"
              title={lang === 'fr' ? 'Film de mariage' : 'Wedding film'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              allowFullScreen
            />
          </VideoRatio>
        </VideoInner>
      </VideoSection>

      {/* Testimonial section (full-bleed over image) */}
      <TestimonialSection>
        <TestimonialOverlay />
        <TestimonialInner>
          <TestimonialText>{review.text}</TestimonialText>
          <TestimonialAuthor>{review.author}</TestimonialAuthor>
        </TestimonialInner>
      </TestimonialSection>

      {/* Distinctions Row (logos) */}
      <Distinctions>
        <DistRow>
          <DistBadge src="/images/QUEEN FOR A DAY.gif" alt="Queen For A Day" />
          <DistBadge src="/images/MELLE DENTELLE.webp" alt="Melle Dentelle" />
          <DistBadge src="/images/LME.webp" alt="LME" />
          <DistBadge src="/images/UN BEAU JOUR.webp" alt="Un Beau Jour" />
        </DistRow>
        <DistBar />
      </Distinctions>

      {/* Process & Investment (modeled after ExperiencePage) */}
      <ProcessWrap>
        <ProcessHeader>{lang === 'fr' ? 'Façonnez votre histoire' : 'Shaping your story'}</ProcessHeader>
        <ProcessGrid>
          <div>
            <Hint>{lang === 'fr' ? 'Cliquez sur les titres pour en savoir plus !' : 'Click on the titles to see more!'}</Hint>
            <Accordion>
              {steps.map((s, i) => (
                <Item key={i}>
                  <ItemHeader onClick={() => setOpenIndex(openIndex === i ? -1 : i)}>
                    {s.title}
                    <Icon>{openIndex === i ? '−' : '+'}</Icon>
                  </ItemHeader>
                  {openIndex === i && (
                    <ItemBody>{s.body}</ItemBody>
                  )}
                </Item>
              ))}
            </Accordion>
          </div>
          <div>
            <ProcessImage src="/images/Wedding videographer - Lino Ludovic (2).webp" alt="wedding videographer process" />
          </div>
        </ProcessGrid>
        {/* In-grid follow-up video (single column) */}
        <VideoInner>
          <VideoRatio>
            <VideoIframe
              src="https://www.youtube.com/embed/6zXjXIHlskI?si=D_veuOJqRl4-Otgg"
              title={lang === 'fr' ? 'Film de mariage' : 'Wedding film'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              allowFullScreen
            />
          </VideoRatio>
        </VideoInner>
      </ProcessWrap>

      <Investment>
        <InvestGrid>
          <div>
            <InvestImage src="/images/Wedding videographer - Lino Ludovic (1).webp" alt="French wedding videographer and photographer" />
          </div>
          <InvestText>
            <InvestTitle>{lang === 'fr' ? 'Investissement' : 'Investment'}</InvestTitle>
            <InvestP>
              {lang === 'fr'
                ? "Chaque histoire est unique et mérite d’être préservée avec une attention rare. L’alliance de la photographie et du film n’offre pas seulement des images, mais une narration complète qui tisse émotions, voix et atmosphères en un héritage singulier."
                : 'Every story is unique and deserves to be preserved with rare care. Combining photography and film offers not just images, but a complete narrative that weaves emotions, voices, and atmospheres into a singular legacy.'}
            </InvestP>
            <InvestP>
              {lang === 'fr'
                ? "La plupart des couples investissent entre 6 800 € et 12 700 € pour cette expérience complète, réunissant photographie et cinéma sur plusieurs jours, avec la même exigence, précision et raffinement intemporel."
                : 'Most couples invest between €6,800 and €12,700 for this comprehensive experience, which includes both photography and cinematography on several days, curated with the same precision, artistry, and timeless refinement.'}
            </InvestP>
            <InvestButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate(pathFor('contact', lang))}>
              {lang === 'fr' ? 'Demander une offre' : 'Inquire'}
            </InvestButton>
          </InvestText>
        </InvestGrid>
      </Investment>
      {/* Contact block (same component used site‑wide) */}
      <ContactSection />
    </Page>
  );
};

export default VideographerPage;
